import { useCallback, useContext, useEffect, useRef } from "react";
import { AppContext } from "../../context/AppStore";
import { NETWORKS, endpointFor } from "../../context/AppStore";

export const NETWORK_LABEL = {
  [NETWORKS.MAINNET]: "Mainnet",
  [NETWORKS.TESTNET]: "Testnet",
  [NETWORKS.DEVNET]: "Devnet",
};

/**
 * The toggle's other network (Devnet ⇄ Mainnet), if it has an account at
 * `address` — for a "that token is on Mainnet" hint when the current
 * network has none. Null when it doesn't, or its RPC can't be reached.
 */
export async function otherNetworkWith(address, network, customRpc) {
  const other = network === NETWORKS.MAINNET ? NETWORKS.DEVNET : NETWORKS.MAINNET;
  const url = endpointFor(other, customRpc);
  if (!url) return null;
  try {
    // Loaded on demand: the marketing site's navbar imports this file for
    // useNetwork, and a static import here put @solana/web3.js — and with
    // it every @solana/* package, which share one vendor chunk — on every
    // docs, blog and tools page. Inside the tool suite it's already loaded.
    const { Connection, PublicKey } = await import("@solana/web3.js");
    const account = await new Connection(url, "confirmed").getAccountInfo(new PublicKey(address));
    return account ? other : null;
  } catch {
    return null;
  }
}

// Maps our internal network id to Solscan's ?cluster= value:
// no param for mainnet, ?cluster=devnet/testnet otherwise.
const SOLSCAN_CLUSTER = {
  [NETWORKS.MAINNET]: null,
  [NETWORKS.TESTNET]: "testnet",
  [NETWORKS.DEVNET]: "devnet",
};

export function useNetwork() {
  const [state, dispatch] = useContext(AppContext);
  const network = state?.currentNetwork || NETWORKS.MAINNET;

  return {
    network,
    endpoint: state?.currentNetworkURL,
    customRpcs: state?.customRpc || {},
    isMainnet: network === NETWORKS.MAINNET,
    setNetwork: (next) => dispatch({ type: "SET_NETWORK", payload: next }),
    // per-network custom RPC (empty string = using the public default)
    customRpc: state?.customRpc?.[network] || "",
    isCustomRpc: !!state?.customRpc?.[network],
    setRpc: (url) => dispatch({ type: "SET_RPC", payload: { network, url } }),
    clearRpc: () => dispatch({ type: "SET_RPC", payload: { network, url: "" } }),
  };
}

/**
 * Calls `onChange` when the Devnet/Mainnet toggle, or that network's RPC,
 * changes while the page is open — never on mount. Lookup tools fetch on a
 * button press and keep the answer in state, so without this a toggle left
 * the other network's result on screen and looked like it did nothing.
 * `onChange` runs after the render that switched, so the `connection` it
 * closes over is already the new network's.
 */
export function useOnNetworkChange(onChange) {
  const [state] = useContext(AppContext);
  const key = `${state?.currentNetwork}|${state?.currentNetworkURL}`;
  const latest = useRef(onChange);
  latest.current = onChange;
  const seen = useRef(key);
  useEffect(() => {
    if (seen.current === key) return;
    seen.current = key;
    latest.current();
  }, [key]);
}

/**
 * Tags each run of a lookup: `const isCurrent = beginRun()`, then only
 * write state while `isCurrent()`. A lookup still in flight when the
 * network switches (or the user runs another) would otherwise land last
 * and overwrite the newer result. Calling `beginRun()` with no lookup
 * after it just cancels the one in flight.
 */
export function useLatestRun() {
  const counter = useRef(0);
  return useCallback(() => {
    counter.current += 1;
    const id = counter.current;
    return () => id === counter.current;
  }, []);
}

// Every explorer link in the tools goes to Solscan.
export function solscanTx(signature, network) {
  const cluster = SOLSCAN_CLUSTER[network];
  const q = cluster ? `?cluster=${cluster}` : "";
  return `https://solscan.io/tx/${signature}${q}`;
}

export function solscanAddress(address, network) {
  const cluster = SOLSCAN_CLUSTER[network];
  const q = cluster ? `?cluster=${cluster}` : "";
  return `https://solscan.io/account/${address}${q}`;
}

export const explorerTx = solscanTx;
export const explorerAddress = solscanAddress;

/** A base58 tx signature, if one is embedded in an error/message string. */
export function extractSignature(text) {
  if (!text) return "";
  const parenthesized = String(text).match(
    /\(([1-9A-HJ-NP-Za-km-z]{64,90})\)\s*$/
  );
  if (parenthesized) return parenthesized[1];
  const anywhere = String(text).match(/[1-9A-HJ-NP-Za-km-z]{80,90}/);
  return anywhere ? anywhere[0] : "";
}
